import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ApiError from '../api/ApiError';
import { deliveryGeographyKey, normalizeDeliveryGeography, setDeliveryGeography } from '../api/client';
import * as serviceContextApi from '../api/endpoints/serviceContext';
import * as placesApi from '../api/endpoints/places';
import useCurrentLocation from '../hooks/useCurrentLocation';
import { labelForReverseGeocode, toPincode } from '../utils/deliveryLocationLabels';

const DeliveryLocationContext = createContext(null);

export const DELIVERY_LOCATION_STORAGE_KEY = 'fnh_delivery_location';
const STORAGE_SCHEMA = 1;
export const DELIVERY_LOCATION_SOURCES = Object.freeze(['gps', 'address', 'search', 'pincode']);
const MAX_LABEL_LENGTH = 200;
const MAX_ADDRESS_ID_LENGTH = 64;
// Returning to the tab re-asks the backend (stock and outlet hours move), but
// focus and visibilitychange often fire together and tabs are switched often.
export const RETURN_REFRESH_MIN_INTERVAL_MS = 30 * 1000;
// Set once an automatic attempt has failed in this tab, so reloads do not
// re-trigger the browser prompt; only the customer's own tap retries.
export const AUTO_DETECT_FAILED_KEY = 'fnh_location_autodetect_failed';
export const REVERSE_GEOCODE_TIMEOUT_MS = 5000;

const hasGeolocation = () => typeof navigator !== 'undefined' && Boolean(navigator.geolocation);
const readAutoDetectFailure = () => {
  try {
    return sessionStorage.getItem(AUTO_DETECT_FAILED_KEY);
  } catch {
    return null;
  }
};
const writeAutoDetectFailure = (reason) => {
  try {
    if (reason) sessionStorage.setItem(AUTO_DETECT_FAILED_KEY, reason);
    else sessionStorage.removeItem(AUTO_DETECT_FAILED_KEY);
  } catch {
    // Storage unavailable: at worst a reload tries automatically once more.
  }
};

/**
 * Current-location detection, for the browsing location only:
 *   idle       not running (a location exists, or detection is off)
 *   detecting  asking the browser / reverse-geocoding
 *   failed     no usable location; `reason` is unsupported | denied |
 *              unavailable | timeout | unconfirmed (position found, area not)
 */
export const IDLE_DETECTION = Object.freeze({ status: 'idle', reason: null });

// The browsing location is geography the customer chose, never an outlet. The
// backend decides which outlet serves it on every request; `serviceContext` is
// its latest answer, kept in memory only and re-asked after every reload.
const UNKNOWN_LOCATION = Object.freeze({
  status: 'unknown', // unknown | resolving | set
  source: 'none',
  pincode: null,
  coordinates: null,
  label: null,
  addressId: null,
  confirmedByUser: false,
  locationKey: '',
  serviceContext: null,
  serviceContextError: null,
});

const cleanText = (value, maxLength) => (
  typeof value === 'string' && value.trim() ? value.trim().slice(0, maxLength) : null
);

// Geography plus the metadata worth restoring. Returns null for anything that
// is not a usable location, so bad input can never become the active one.
const toLocation = (input) => {
  const geography = normalizeDeliveryGeography(input);
  if (!geography || !DELIVERY_LOCATION_SOURCES.includes(input?.source)) return null;
  return {
    ...geography,
    source: input.source,
    label: cleanText(input.label, MAX_LABEL_LENGTH),
    addressId: cleanText(input.addressId, MAX_ADDRESS_ID_LENGTH),
    confirmedByUser: input.confirmedByUser === true,
  };
};

// Only these fields are written. The resolved outlet is deliberately absent:
// a restored location must always be re-resolved by the backend.
const persistLocation = (location) => {
  try {
    if (!location) {
      localStorage.removeItem(DELIVERY_LOCATION_STORAGE_KEY);
      return;
    }
    const { pincode, coordinates, label, source, addressId, confirmedByUser } = location;
    localStorage.setItem(
      DELIVERY_LOCATION_STORAGE_KEY,
      JSON.stringify({ schema: STORAGE_SCHEMA, pincode, coordinates, label, source, addressId, confirmedByUser })
    );
  } catch {
    // Storage unavailable (private mode, quota) — the location still applies
    // for this page view, it just will not survive a reload.
  }
};

const readStoredLocation = () => {
  try {
    const raw = localStorage.getItem(DELIVERY_LOCATION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const location = parsed?.schema === STORAGE_SCHEMA ? toLocation(parsed) : null;
    if (!location) localStorage.removeItem(DELIVERY_LOCATION_STORAGE_KEY);
    return location;
  } catch {
    try {
      localStorage.removeItem(DELIVERY_LOCATION_STORAGE_KEY);
    } catch {
      // Nothing more to do; start without a location.
    }
    return null;
  }
};

/**
 * What the storefront may show for the current location:
 *   unknown        no browsing geography yet
 *   checking       geography set, no backend answer for it yet
 *   error          geography set, the backend could not be asked
 *   unserviceable  the backend says no outlet delivers there
 *   ready          the backend answered and an outlet delivers there
 * A refresh of the same geography keeps its previous answer, so it stays
 * `ready` rather than flickering the catalogue away.
 */
export const storefrontStatusFor = ({ locationKey, serviceContext, serviceContextError }, detection = IDLE_DETECTION) => {
  // Still finding the customer's position: shown like a pending backend
  // answer, so neither "Select location" nor a catalogue flashes meanwhile.
  if (!locationKey) return detection.status === 'detecting' ? 'checking' : 'unknown';
  if (serviceContext) return serviceContext.serviceable === true ? 'ready' : 'unserviceable';
  return serviceContextError ? 'error' : 'checking';
};

const toServiceContextError = (err) => ({
  message: err?.message || 'Could not check delivery for this location.',
  code: err?.code || null,
  status: err?.status ?? null,
});

export const DeliveryLocationProvider = ({ children, autoDetect = false }) => {
  const [state, setState] = useState(() => {
    const stored = readStoredLocation();
    // Applied during initialization, not in an effect: child effects run
    // before this provider's, so the first catalogue requests after a reload
    // would otherwise leave without the restored geography. Idempotent.
    setDeliveryGeography(stored);
    if (!stored) return { ...UNKNOWN_LOCATION, locationVersion: 0 };
    return {
      ...UNKNOWN_LOCATION,
      ...stored,
      status: 'resolving',
      locationKey: deliveryGeographyKey(stored),
      locationVersion: 0,
    };
  });
  const [refreshCount, setRefreshCount] = useState(0);
  // Decided for the first render, so a first visit shows the detecting state
  // straight away instead of flashing "Select location".
  const [detection, setDetection] = useState(() => {
    if (!autoDetect || readStoredLocation()) return IDLE_DETECTION;
    if (!hasGeolocation()) return { status: 'failed', reason: 'unsupported' };
    const failedBefore = readAutoDetectFailure();
    if (failedBefore) return { status: 'failed', reason: failedBefore };
    return { status: 'detecting', reason: null };
  });
  const { captureLocation } = useCurrentLocation();
  // Each detection takes a generation; a newer one, or any explicit location
  // choice, makes an in-flight detection's result land nowhere.
  const detectGenerationRef = useRef(0);
  const [locationPromptOpen, setLocationPromptOpen] = useState(false);
  // When the latest resolution was started, for throttling return refreshes.
  const lastResolveStartedAtRef = useRef(0);
  const stateRef = useRef(state);
  stateRef.current = state;
  // Every resolution takes a generation; only the latest may write state.
  const resolveGenerationRef = useRef(0);

  const { locationKey } = state;

  useEffect(() => {
    const generation = ++resolveGenerationRef.current;
    if (!locationKey) return undefined;

    const current = stateRef.current;
    const geography = { pincode: current.pincode, coordinates: current.coordinates };
    const isCurrent = (prev) => generation === resolveGenerationRef.current && prev.locationKey === locationKey;
    lastResolveStartedAtRef.current = Date.now();

    setState((prev) => (
      isCurrent(prev) && prev.status !== 'resolving'
        ? { ...prev, status: 'resolving', serviceContextError: null }
        : prev
    ));

    // Pinned to this location's geography, so the request describes exactly
    // the place being resolved even if the global value moves meanwhile.
    serviceContextApi
      .getServiceContext(geography)
      .then((response) => {
        if (generation !== resolveGenerationRef.current) return;
        const data = response?.data;
        // The backend ignores geography it cannot parse and answers for the
        // saved address instead. Never store that as this location's answer.
        if (!data || typeof data !== 'object' || data.source !== 'header') {
          throw new ApiError({
            message: 'Could not check delivery for this location.',
            code: 'GEOGRAPHY_NOT_APPLIED',
          });
        }
        setState((prev) => (
          isCurrent(prev) ? { ...prev, status: 'set', serviceContext: data, serviceContextError: null } : prev
        ));
      })
      .catch((err) => {
        if (generation !== resolveGenerationRef.current) return;
        // A new geography always starts with serviceContext null, so a kept
        // answer here can only be this same geography's previous one: a
        // failed refresh must not wipe a storefront that was just valid.
        setState((prev) => (
          isCurrent(prev)
            ? { ...prev, status: 'set', serviceContextError: toServiceContextError(err) }
            : prev
        ));
      });

    return () => {
      // Unmount or a newer location: this response must not land.
      if (resolveGenerationRef.current === generation) resolveGenerationRef.current += 1;
    };
  }, [locationKey, refreshCount]);

  /**
   * Make `input` the browsing location. Returns false (and changes nothing)
   * when it holds no usable geography or an unknown source. A new geography
   * bumps `locationVersion` and re-resolves; the same geography only updates
   * its label/source/addressId/confirmedByUser.
   */
  const applyLocation = useCallback((input) => {
    const location = toLocation(input);
    if (!location) return false;
    const key = deliveryGeographyKey(location);

    setDeliveryGeography(location);
    persistLocation(location);
    setState((prev) => {
      if (prev.locationKey === key) return { ...prev, ...location };
      return {
        ...UNKNOWN_LOCATION,
        ...location,
        status: 'resolving',
        locationKey: key,
        locationVersion: prev.locationVersion + 1,
      };
    });
    return true;
  }, []);

  // Any location chosen elsewhere (dialog, saved address, "Use this
  // location") wins over a detection still in flight.
  const setDeliveryLocation = useCallback((input) => {
    const accepted = applyLocation(input);
    if (accepted) {
      detectGenerationRef.current += 1;
      setDetection((prev) => (prev.status === 'idle' ? prev : IDLE_DETECTION));
    }
    return accepted;
  }, [applyLocation]);

  /**
   * Ask the browser for the current position and make it the browsing
   * location. `automatic` is the startup attempt: it never replaces a location
   * that appeared meanwhile and is stored as not confirmed by the customer.
   * From a click, getCurrentPosition runs inside that user gesture. The
   * backend then decides serviceability and outlet from the geography alone.
   *
   * Every "use my location" goes through here — startup, the retry button and
   * the location dialog — so a position whose area cannot be named gets the
   * same recovery wherever it was asked for. `stillWanted` lets a caller that
   * went away (a closed dialog) drop the result before anything is applied.
   *
   * Resolves to { key } — the geography key applied — or { key: null, reason }:
   * a failure reason (denied | unavailable | timeout | unsupported), or null
   * when the result was superseded or no longer wanted.
   */
  const detectCurrentLocation = useCallback(async ({ automatic = false, stillWanted = () => true } = {}) => {
    const generation = ++detectGenerationRef.current;
    const isCurrent = () => generation === detectGenerationRef.current;
    const dropped = () => {
      if (isCurrent()) setDetection(IDLE_DETECTION);
      return { key: null, reason: null };
    };
    setDetection({ status: 'detecting', reason: null });
    const { coordinates, errorCode } = await captureLocation({ withReason: true });
    if (!isCurrent()) return { key: null, reason: null };
    if (!stillWanted()) return dropped();
    if (!coordinates) {
      const reason = errorCode || 'unavailable';
      if (automatic) writeAutoDetectFailure(reason);
      setDetection({ status: 'failed', reason });
      return { key: null, reason };
    }
    // A readable place is nice to have: /service-context resolves coordinates
    // alone, so a failed lookup never makes a location unserviceable.
    // Capped, so a slow or hung lookup can never hold the loader open.
    let address = null;
    let lookupTimer;
    try {
      const lookup = placesApi.reverseGeocode(coordinates);
      const cap = new Promise((resolve) => {
        lookupTimer = setTimeout(() => resolve(null), REVERSE_GEOCODE_TIMEOUT_MS);
      });
      address = (await Promise.race([lookup, cap]))?.data || null;
    } catch {
      address = null;
    } finally {
      clearTimeout(lookupTimer);
    }
    if (!isCurrent()) return { key: null, reason: null };
    if (!stillWanted() || (automatic && stateRef.current.locationKey)) return dropped();
    const accepted = applyLocation({
      coordinates,
      pincode: toPincode(address?.pincode),
      label: labelForReverseGeocode(address) || 'Current location',
      source: 'gps',
      confirmedByUser: !automatic,
    });
    const key = accepted ? deliveryGeographyKey({ coordinates, pincode: toPincode(address?.pincode) }) : null;
    writeAutoDetectFailure(accepted ? null : 'unavailable');
    setDetection(accepted ? IDLE_DETECTION : { status: 'failed', reason: 'unavailable' });
    return accepted ? { key, reason: null } : { key: null, reason: 'unavailable' };
  }, [captureLocation, applyLocation]);

  // A detected position whose area could not be named (no pincode from the
  // address lookup) that the backend cannot serve by coordinates alone: many
  // outlets deliver by pincode, so this is "unconfirmed", not "unserviceable".
  // The customer is asked to choose the location instead.
  //
  // Decided by the geography itself — a GPS position with no pincode — not by
  // who applied it or whether the customer accepted it: "Use this location",
  // a reload, or the dialog all leave the position exactly as insufficient
  // for a pincode-based answer. A location with a pincode (or chosen any
  // other way) keeps the backend's unserviceable answer as is.
  useLayoutEffect(() => {
    const { locationKey: key, pincode, source, serviceContext } = state;
    if (!key || source !== 'gps' || pincode || serviceContext?.serviceable !== false) return;
    setDeliveryGeography(null);
    persistLocation(null);
    setState((prev) => (prev.locationKey === key ? { ...UNKNOWN_LOCATION, locationVersion: prev.locationVersion + 1 } : prev));
    setDetection({ status: 'failed', reason: 'unconfirmed' });
  }, [state]);

  // Startup bootstrap, once, and only with no browsing location at all. A
  // restored location (chosen or detected earlier) is used as is: no GPS.
  const detectRef = useRef(detectCurrentLocation);
  detectRef.current = detectCurrentLocation;
  useEffect(() => {
    if (detection.status !== 'detecting' || stateRef.current.locationKey) return undefined;
    let active = true;
    let permission = null;
    const onPermissionChange = () => {
      // Allowed later from the browser's site settings: pick it up, still
      // without overriding a location chosen in the meantime.
      if (permission?.state === 'granted' && !stateRef.current.locationKey) {
        writeAutoDetectFailure(null);
        detectRef.current({ automatic: true });
      }
    };
    const run = async () => {
      try {
        if (navigator.permissions?.query) {
          permission = await navigator.permissions.query({ name: 'geolocation' });
          if (!active) return;
          permission.addEventListener?.('change', onPermissionChange);
          if (permission.state === 'denied') {
            // Never call geolocation when the site is blocked.
            writeAutoDetectFailure('denied');
            setDetection({ status: 'failed', reason: 'denied' });
            return;
          }
        }
      } catch {
        // No Permissions API for geolocation (older Safari): just ask for the
        // position; the browser shows its own prompt if it needs one.
      }
      // granted: no prompt appears. prompt: the browser's native dialog does.
      if (active) detectRef.current({ automatic: true });
    };
    run();
    return () => {
      active = false;
      permission?.removeEventListener?.('change', onPermissionChange);
    };
    // The startup attempt only: later attempts are the customer's own taps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearDeliveryLocation = useCallback(() => {
    setDeliveryGeography(null);
    persistLocation(null);
    setState((prev) => (
      prev.locationKey ? { ...UNKNOWN_LOCATION, locationVersion: prev.locationVersion + 1 } : prev
    ));
  }, []);

  // Ask the backend again for the same geography (e.g. after a failure). The
  // current serviceContext stays visible until the new answer arrives.
  const refreshServiceContext = useCallback(() => {
    if (!stateRef.current.locationKey) return;
    // Marked resolving in the same update, so no consumer sees the previous
    // answer (or error) as the result of this refresh.
    setState((prev) => (
      prev.locationKey ? { ...prev, status: 'resolving', serviceContextError: null } : prev
    ));
    setRefreshCount((count) => count + 1);
  }, []);

  // Coming back to the page: refresh the same geography's answer. Only with a
  // location, never while one is already resolving, and at most once per
  // interval — the timestamp is claimed before the render so the second of a
  // focus + visibilitychange pair is dropped. locationVersion is untouched.
  useEffect(() => {
    const onReturn = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      const { locationKey: key, status } = stateRef.current;
      if (!key || status === 'resolving') return;
      const now = Date.now();
      if (now - lastResolveStartedAtRef.current < RETURN_REFRESH_MIN_INTERVAL_MS) return;
      lastResolveStartedAtRef.current = now;
      setRefreshCount((count) => count + 1);
    };
    document.addEventListener('visibilitychange', onReturn);
    window.addEventListener('focus', onReturn);
    return () => {
      document.removeEventListener('visibilitychange', onReturn);
      window.removeEventListener('focus', onReturn);
    };
  }, []);

  // One location dialog for the whole site; any "Select location" control
  // opens it through here.
  const openLocationPrompt = useCallback(() => setLocationPromptOpen(true), []);
  const closeLocationPrompt = useCallback(() => setLocationPromptOpen(false), []);

  const value = useMemo(
    () => ({
      ...state,
      isServiceable: state.serviceContext ? state.serviceContext.serviceable === true : null,
      storefrontStatus: storefrontStatusFor(state, detection),
      locationDetection: detection,
      detectCurrentLocation,
      setDeliveryLocation,
      clearDeliveryLocation,
      refreshServiceContext,
      locationPromptOpen,
      openLocationPrompt,
      closeLocationPrompt,
    }),
    [
      state,
      detection,
      detectCurrentLocation,
      setDeliveryLocation,
      clearDeliveryLocation,
      refreshServiceContext,
      locationPromptOpen,
      openLocationPrompt,
      closeLocationPrompt,
    ]
  );

  return <DeliveryLocationContext.Provider value={value}>{children}</DeliveryLocationContext.Provider>;
};

/**
 * For providers and shared widgets that also render standalone (tests, or
 * outside the storefront tree): null when there is no DeliveryLocationProvider.
 * In the app the provider always exists; prefer useDeliveryLocation elsewhere.
 */
export const useOptionalDeliveryLocation = () => useContext(DeliveryLocationContext);

export const useDeliveryLocation = () => {
  const ctx = useContext(DeliveryLocationContext);
  if (!ctx) throw new Error('useDeliveryLocation must be used within a DeliveryLocationProvider');
  return ctx;
};
